import React, { useEffect, useRef, useState } from "react";
import useAxios from "../../utils/useAxios";
import { showModal, hideModal } from "../../utils/modalUtility";

const DailyQuotes = () => {
  const axiosAPI = useAxios();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentQuoteId, setCurrentQuoteId] = useState(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const imageRef = useRef(null);

  // Only admin can access this page (route protected)

  const buildQuery = () => {
    const params = new URLSearchParams();

    params.append("page", page);
    return params.toString();
  };

  // Fetch daily quotes list
  const fetchQuotes = async () => {
    setLoading(true);
    const queryString = buildQuery();
    try {
      const res = await axiosAPI.get(`/api/daily-quote/get-all?${queryString}`);
      setQuotes(res.data?.results || res.data || []);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      setErrorMessage("Failed to fetch daily quotes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
    // eslint-disable-next-line
  }, [page]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const resetForm = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setTitle("");
    setDescription("");
    setImageFile(null);
    setPreviewUrl("");
    setIsEditMode(false);
    setCurrentQuoteId(null);
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setErrorMessage("");
    setSuccessMessage("");
    imageRef.current.value = null;
    showModal("newDailyQuoteModal");
  };

  const openEditModal = (id) => {
    setIsEditMode(true);
    setCurrentQuoteId(id);
    const selectedQuote = quotes.find((q) => q.id === id || q._id === id);
    if (selectedQuote) {
      setTitle(selectedQuote.title);
      setDescription(selectedQuote.description || "");
      setPreviewUrl(selectedQuote.image_url);
      setImageFile(null);
    }
    setErrorMessage("");
    setSuccessMessage("");
    showModal("newDailyQuoteModal");
  };

  const closeModal = () => {
    resetForm();
    hideModal("newDailyQuoteModal");
  };

  const handlePageClick = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setPage(pageNum);
    }
  };

  const handleImageChange = (event) => {
    const selectedFile = event.target.files?.[0];
    setErrorMessage("");
    setSuccessMessage("");
    if (!selectedFile) return;
    if (!selectedFile.type.startsWith("image/")) {
      setImageFile(null);
      setPreviewUrl("");
      setErrorMessage("Please upload a valid image file.");
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setImageFile(selectedFile);
    setPreviewUrl(URL.createObjectURL(selectedFile));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    if (!title.trim()) {
      setErrorMessage("Title is required.");
      return;
    }
    const hasExistingImage = isEditMode && Boolean(previewUrl);
    if (!description.trim() && !imageFile && !hasExistingImage) {
      setErrorMessage("Please provide description or image.");
      return;
    }
    const formData = new FormData();
    formData.append("title", title.trim());
    formData.append("description", description.trim());
    if (imageFile) {
      formData.append("image", imageFile);
    }
    try {
      setIsSubmitting(true);
      let response;
      if (isEditMode) {
        response = await axiosAPI.put(
          `/api/daily-quote/update/${currentQuoteId}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        window.notifyMessage(
          "Success",
          "Daily quote updated successfully",
          "success",
        );
      } else {
        response = await axiosAPI.post("/api/daily-quote/add", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        window.notifyMessage(
          "Success",
          "Daily quote added successfully",
          "success",
        );
      }
      resetForm();
      hideModal("newDailyQuoteModal");
      fetchQuotes();
      imageRef.current.value = null;
    } catch (error) {
      const apiError =
        error?.response?.data?.message || "Failed to save daily quote.";
      setErrorMessage(apiError);
      if (typeof window.notifyMessage === "function") {
        window.notifyMessage("Error", apiError, "error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete logic
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this quote?")) return;
    setDeletingId(id);
    try {
      await axiosAPI.delete(`/api/daily-quote/delete/${id}`);
      if (typeof window.notifyMessage === "function") {
        window.notifyMessage(
          "Success",
          "Quote deleted successfully",
          "success",
        );
      }
      fetchQuotes();
    } catch (err) {
      setErrorMessage("Failed to delete quote.");
      if (typeof window.notifyMessage === "function") {
        window.notifyMessage("Error", "Failed to delete quote.", "error");
      }
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="container">
      <div className="page-inner">
        <div className="d-flex justify-content-end align-items-center flex-wrap gap-2 mb-4">
          <button
            type="button"
            className="btn btn-dark btn-round"
            onClick={openAddModal}
          >
            Add Daily Quote
          </button>
        </div>

        <div
          className="modal fade"
          id="newDailyQuoteModal"
          tabIndex="-1"
          aria-labelledby="newDailyQuoteModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newDailyQuoteModalLabel">
                  {isEditMode ? "Edit Daily Quote" : "Add Daily Quote"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>

              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="dailyQuoteTitle" className="form-label">
                      Quote Title
                    </label>
                    <input
                      id="dailyQuoteTitle"
                      name="title"
                      type="text"
                      className="form-control"
                      value={title}
                      maxLength={160}
                      placeholder="Enter quote title"
                      onChange={(event) => setTitle(event.target.value)}
                      disabled={isSubmitting}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label
                      htmlFor="dailyQuoteDescription"
                      className="form-label"
                    >
                      Description
                    </label>
                    <textarea
                      id="dailyQuoteDescription"
                      name="description"
                      className="form-control"
                      rows="3"
                      placeholder="Enter description"
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      disabled={isSubmitting}
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="dailyQuoteImage" className="form-label">
                      Quote Image
                    </label>
                    <input
                      id="dailyQuoteImage"
                      name="image"
                      type="file"
                      className="form-control"
                      accept="image/*"
                      ref={imageRef}
                      onClick={() => (imageRef.current.value = null)}
                      onChange={handleImageChange}
                      disabled={isSubmitting}
                    />
                    <small className="text-muted">
                      One field is required: either upload an image or provide a
                      description.
                    </small>
                  </div>

                  {previewUrl && (
                    <div className="mb-3">
                      <label className="form-label">Preview</label>
                      <div className="border rounded p-2 bg-light text-center">
                        <img
                          src={previewUrl}
                          alt="Daily quote preview"
                          style={{ maxWidth: "100%", maxHeight: "260px" }}
                        />
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary btn-round"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {isEditMode ? "Updating..." : "Adding..."}
                      </>
                    ) : isEditMode ? (
                      "Update Daily Quote"
                    ) : (
                      "Add Daily Quote"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title mb-1">
                  All Daily Quotes ({quotes.length})
                </h4>
                <p className="mb-0 text-muted">
                  Use the Add Daily Quote button to upload title and image.
                </p>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Description</th>
                        <th>Image</th>
                        <th>Created</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={5} className="text-center">
                            <div
                              className="spinner-border text-success"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : quotes.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center">
                            No Daily Quotes Found
                          </td>
                        </tr>
                      ) : (
                        quotes.length > 0 &&
                        quotes?.map((q) => (
                          <tr key={q.id || q._id}>
                            <td>{q.title}</td>
                            <td>{q.description || "-"}</td>
                            <td>
                              {q.image_url ? (
                                <img
                                  src={q.image_url}
                                  alt={q.title}
                                  style={{
                                    maxWidth: 90,
                                    maxHeight: 60,
                                    borderRadius: 6,
                                  }}
                                />
                              ) : (
                                <span className="text-muted">No Image</span>
                              )}
                            </td>
                            <td>
                              {q.created_at
                                ? new Date(q.created_at).toLocaleDateString()
                                : "-"}
                            </td>
                            <td>
                              <button
                                className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                onClick={() => openEditModal(q.id || q._id)}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                className="btn btn-icon btn-round btn-danger btn-sm"
                                disabled={deletingId === (q.id || q._id)}
                                onClick={() => handleDelete(q.id || q._id)}
                              >
                                {deletingId === (q.id || q._id) ? (
                                  <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                  <i className="fas fa-trash"></i>
                                )}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <nav aria-label="Page navigation">
              <ul className="pagination flex justify-content-end">
                {/* First Page */}
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    First
                  </button>
                </li>

                {/* Previous */}
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(page - 1)}
                  >
                    Previous
                  </button>
                </li>

                {/* Page Number Buttons in chunks */}
                {(() => {
                  const pageNumbers = [];
                  const maxPagesToShow = 5;
                  const startPage =
                    Math.floor((page - 1) / maxPagesToShow) * maxPagesToShow +
                    1;
                  const endPage = Math.min(
                    startPage + maxPagesToShow - 1,
                    totalPages,
                  );

                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <li
                        key={i}
                        className={`page-item ${page === i ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageClick(i)}
                        >
                          {i}
                        </button>
                      </li>,
                    );
                  }
                  return pageNumbers;
                })()}

                {/* Next */}
                <li
                  className={`page-item ${
                    page === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(page + 1)}
                  >
                    Next
                  </button>
                </li>

                {/* Last Page */}
                <li
                  className={`page-item ${
                    page === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    Last
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyQuotes;
